// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + inner white surfaces with soft blue border
// - Left orange accent stripe for Student Details section
// - Tables: blue header, soft zebra rows, hover tint, subtle borders
// - Totals: blue band consistent with theme
// - No API/business logic changed (only UI/styling + a couple of invalid TableHead structures fixed)

import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  Snackbar,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { API_URL, API_UPLOAD_URL } from "./config";

const ReportStudentMarks = () => {
  const navigate = useNavigate();
  const [studentList, setStudentList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [studentDetails, setStudentDetails] = useState(null);
  const [marksBySemester, setMarksBySemester] = useState({});
  const [logincampusCode, setCampusCode] = useState("");
  const [studentkey, setStudentKey] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const token = localStorage.getItem("auth");

  // ✅ Theme constants (same as your ManageUsers/Hostel theme)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const tableWrapSx = {
    ...surfaceSx,
    overflow: "hidden",
    overflowX: "auto",     // ✅ allow left-right scroll
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch", // ✅ smooth iOS scrolling
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 0.8 },
  });

  const subHeaderRowSx = {
    backgroundColor: "rgba(25,118,210,0.08)",
    "& td": {
      fontWeight: 900,
      color: "rgba(25,118,210,0.95)",
      borderBottom: "1px solid rgba(25,118,210,0.12)",
      py: 1,
    },
  };

  // Extract CampusCode from JWT
  useEffect(() => {
    if (token) {
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        setStudentKey(decodedToken.studentkey);
        setCampusCode(decodedToken.campusCode);
      } catch (err) {
        console.error("Error decoding token:", err);
        setCampusCode("");
      }
    }
  }, []);

  // (kept as-is, even though it's redundant)
  useEffect(() => {
    if (token) {
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        setCampusCode(decodedToken.campusCode);
      } catch (err) {
        console.error("Error decoding token:", err);
        setCampusCode("");
      }
    }
  }, []);

  useEffect(() => {
    if (logincampusCode) {
      fetchStudentMarks();
    }
  }, [logincampusCode]);

  const fetchStudentMarks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dataReportStudentMarksStudent`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;
      if (data) {
        setStudentDetails(data.studentDetails);
        setMarksBySemester(data.marksBySemester || {});
      } else {
        setStudentDetails(null);
        setMarksBySemester({});
      }
    } catch (error) {
      console.error("Error fetching student marks:", error);
      if (error.response && error.response.status === 401) {
        navigate("/");
      } else {
        setError("Failed to fetch student marks");
      }
    }
  };

  // kept
  const handleSearch = () => {
    if (selectedStudent) {
      fetchStudentMarks(selectedStudent.StudentID);
    } else {
      setStudentDetails(null);
      setMarksBySemester({});
    }
  };

  // kept
  const handleLogout = () => {
    localStorage.removeItem("auth");
    navigate("/");
  };

  // kept
  const handleGoHome = () => {
    navigate("/home");
  };

  const convertToISTDate = (utcTime) => {
    const date = new Date(utcTime).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
    return date;
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Student Marks
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Semester-wise marks with SGPA / CGPA summary
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* Messages */}
          {error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                mb: 2,
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess(false)}>
              <Alert severity="success" sx={{ borderRadius: 2 }} onClose={() => setSuccess(false)}>
                Operation successful!
              </Alert>
            </Snackbar>
          )}

          {/* ✅ Student Details */}
          {studentDetails && (
            <Paper variant="outlined" sx={sectionSx}>
              <Box sx={{ pl: 1 }}>
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                  Student Details
                </Typography>
                <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

                <Table size="small" sx={{ width: "100%" }}>
                  <TableBody>
                    <TableRow sx={zebraRowSx(0)}>
                      <TableCell sx={{ fontWeight: 900, width: 180 }}>Student ID</TableCell>
                      <TableCell>{studentDetails.StudentID}</TableCell>
                      <TableCell sx={{ fontWeight: 900, width: 180 }}>Student Name</TableCell>
                      <TableCell>{studentDetails.FullName}</TableCell>
                    </TableRow>

                    <TableRow sx={zebraRowSx(1)}>
                      <TableCell sx={{ fontWeight: 900 }}>Campus Name</TableCell>
                      <TableCell>{studentDetails.CampusName}</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Degree Name</TableCell>
                      <TableCell>{studentDetails.DegreeName}</TableCell>
                    </TableRow>

                    <TableRow sx={zebraRowSx(2)}>
                      <TableCell sx={{ fontWeight: 900 }}>Branch Name</TableCell>
                      <TableCell>{studentDetails.BranchName}</TableCell>
                      <TableCell />
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          )}

          {/* ✅ Marks by Semester */}
          {Object.entries(marksBySemester).map(([semester, marks]) => {
            const totalCredit = marks.reduce((sum, item) => sum + (item.Credit || 0), 0);

            const GPA = new Set();
            marks.forEach((item) => {
              if (item.GPA) GPA.add(item.GPA);
            });

            return (
              <TableContainer key={semester} component={Paper} sx={{ ...tableWrapSx, mt: 2 }}>
                <Table size="small" sx={{ width: "100%" }}>
                  <TableHead>
                    {/* ✅ Fixed: semester title must be inside a TableRow */}
                    <TableRow sx={subHeaderRowSx}>
                      <TableCell colSpan={6} align="center" sx={{ fontSize: 16 }}>
                        Semester {semester}
                      </TableCell>
                    </TableRow>

                    <TableRow sx={theadRowSx}>
                      <TableCell>Subject Code</TableCell>
                      <TableCell>Subject Name</TableCell>
                      <TableCell>Credits</TableCell>
                      <TableCell>Grade</TableCell>
                      <TableCell>Grade Point</TableCell>
                      <TableCell>Pass Year</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {marks.map((item, index) => (
                      <TableRow key={index} sx={zebraRowSx(index)}>
                        <TableCell>{item.SubjectCode}</TableCell>
                        <TableCell>{item.SubjectName}</TableCell>
                        <TableCell>{item.Credit}</TableCell>
                        <TableCell>{item.Grade}</TableCell>
                        <TableCell>{item.GradePoint}</TableCell>
                        <TableCell>{item.Year}</TableCell>
                      </TableRow>
                    ))}

                    {/* ✅ Semester totals row */}
                    <TableRow sx={{ backgroundColor: "rgba(25,118,210,0.06)" }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }} align="right">
                        Total Credits: {totalCredit}
                      </TableCell>
                      <TableCell colSpan={2} sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }} align="right">
                        SGPA: {[...GPA].join(", ")}
                      </TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })}

          {/* ✅ Overall totals band */}
          {studentDetails && (
            <Paper variant="outlined" sx={{ ...surfaceSx, mt: 2, overflow: "hidden" }}>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow
                      sx={{
                        backgroundColor: "rgba(25,118,210,0.95)",
                        "& td": { color: "#fff", fontWeight: 900, py: 1.2, borderBottom: "none" },
                      }}
                    >
                      <TableCell align="right">Total Credits: {studentDetails.TotalCredits}</TableCell>
                      <TableCell align="center">CGPA: {studentDetails.CGPA}</TableCell>
                      <TableCell align="left">Percentage: {studentDetails.Percentage}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ReportStudentMarks;
